import { gql } from '@apollo/client';

import {
  WITH_COUNT_VARIABLES,
  WITH_COUNT_ARGUMENTS
} from 'app/src/queries/withCount';

const ERRORS = `errors { path, message }`;

const TXN_CORE_FIELDS = gql`
  fragment TxnCoreFields on Txn {
    id
    type
    method
    reference
    bankDate
    name
    entityName
    memo
    entityType
    entityId
    entityEmail
    createdAt
    updatedAt
    assets { id, name, lastSix, listings, stockNumber, seller { id }}
  }
`;

export const TXN_LINE_FIELDS = gql`
  fragment TxnLineFields on TxnLine {
    id
    account { id, name, special }
    debitLessCredit
    creditLessDebit
    offsetId
    asset { id, soldOn, stockNumber, vin }
    saleAsset { id }
    fee { id, type, description }
    memo
  }
`;

export const TXN_FIELDS = gql`
  ${TXN_CORE_FIELDS}
  fragment TxnFields on Txn {
    ...TxnCoreFields
    amount
    dueOn
    voidBy {
      id
      bankDate
    }
    paymentTerm {
      id
    }
    achBatch {
      id
      bankDate
    }
  }
`;

export const TXN_ACCOUNT_FIELDS = gql`
  fragment TxnAccountFields on Account {
    value: id
    label: fullName
    id
    fullName
    posting
    hiddenAt
    special
  }
`;

export const TXN = gql`
  ${TXN_FIELDS}
  ${TXN_LINE_FIELDS}
  query Txn($id: ID!) {
    txn(id: $id) {
      ...TxnFields
      txnLines {
        ...TxnLineFields
      }
    }
  }
`;

export const TXNS = gql`
  ${TXN_FIELDS}
  ${TXN_LINE_FIELDS}
  ${TXN_ACCOUNT_FIELDS}
  query Txns($scopes: [String!]) {
    txns(scopes: $scopes) {
      ...TxnFields
      txnLines {
        ...TxnLineFields
      }
      createdBy {
        fullName
      }
    }
    accounts(scopes: ["includeHidden"]) {
      ...TxnAccountFields
    }
  }
`;

const TXN_ARGUMENTS = `
  $paymentTermId: ID
  $type: String!
  $method: String
  $reference: String
  $bankDate: ISO8601Date!
  $name: String
  $memo: String
  $entityType: String
  $entityId: ID
  $balanceAccountId: ID
  $addToAchBatch: Boolean
  $txnLinesAttributes: [TxnLineInput!]!
`;

const TXN_VARIABLES = `
  paymentTermId: $paymentTermId
  type: $type
  method: $method
  reference: $reference
  bankDate: $bankDate
  name: $name
  memo: $memo
  entityType: $entityType
  entityId: $entityId
  balanceAccountId: $balanceAccountId
  addToActiveAchBatch: $addToAchBatch
  txnLinesAttributes: $txnLinesAttributes
`;

export const CREATE_TXN = gql`
  ${TXN_CORE_FIELDS}
  mutation CreateTxn (
    ${TXN_ARGUMENTS}
  ) {
    createTxn (
      ${TXN_VARIABLES}
    ) {
      txn {
        ...TxnCoreFields
      }
      ${ERRORS}
    }
  }
`;

export const UPDATE_TXN = gql`
  ${TXN_CORE_FIELDS}
  mutation UpdateTxn (
    $id: ID!
    ${TXN_ARGUMENTS}
  ) {
    updateTxn (
      id: $id
      ${TXN_VARIABLES}
    ) {
      txn {
        ...TxnCoreFields
      }
      ${ERRORS}
    }
  }
`;

export const DESTROY_TXN = gql`
  mutation DestroyTxn ($id: ID!) {
    destroyTxn(id: $id) {
      ${ERRORS}
    }
  }
`;

export const VOID_TXN = gql`
  mutation VoidTxn (
    $id: ID!
    $bankDate: ISO8601Date
    $memo: String
  ) {
    voidTxn(
      id: $id
      bankDate: $bankDate
      memo: $memo
    ) {
      ${ERRORS}
    }
  }
`;

export const UNVOID_TXN = gql`
  mutation UnvoidTxn ($id: ID!) {
    unvoidTxn(id: $id) {
      ${ERRORS}
    }
  }
`;

export const TXNS_WITH_COUNT_FIELDS = `
  id
  type
  method
  reference
  bankDate
  name
  memo
  entityType
  entityId
  entityEmail
  amount
  dueOn
  voidBy {
    id
  }
  paymentTerm {
    id
  }
  txnLines {
    id
    account { id, name, special }
    debitLessCredit
    offsetId
    memo
  }
`;

export const TXNS_WITH_COUNT = gql`
  ${TXN_FIELDS}
  ${TXN_LINE_FIELDS}
  ${TXN_ACCOUNT_FIELDS}
  query (
    $assetId: ID
    ${WITH_COUNT_VARIABLES}
  ) {
    txnsWithCount(
      assetId: $assetId
      ${WITH_COUNT_ARGUMENTS}
    ) {
      entries {
        ...TxnFields
        txnLines {
          ...TxnLineFields
        }
        createdBy {
          fullName
        }
      }
      accounts {
        ...TxnAccountFields
      }
      count
    }
  }
`;

export const CONNECT_TXNS_TO_ENTITY = gql`
  mutation ConnectTxnsToEntity (
    $txnIds: [ID!]!
    $entityType: String!
    $entityId: ID!
  ) {
    connectTxnsToEntity(
      txnIds: $txnIds
      entityType: $entityType
      entityId: $entityId
    ) {
      ${ERRORS}
    }
  }
`;

export const DESTROY_TXN_LINE = gql`
  mutation DestroyTxnLine($id: ID!) {
    destroyTxnLine(id: $id) {
      ${ERRORS}
    }
  }
`;
